import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useGuardiesAra, useTauler, type GuardiaAreaDto } from '@/api/activacions';
import { useSollicituds } from '@/api/planificacio';
import type { ActivacioResumDto } from '@/api/types';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Permis } from '@/permisos/Permis';
import { useMe } from '@/permisos/MeContext';
import { Avatar, BadgeTipus, BotoTrucada, Buit, ProgresConfirmacions } from './comu';
import { colorConfirmacions, minutsDes, textFa, useObertes, useTempsRealTauler } from './suport';

/** Àrees de «De guàrdia ara» visibles abans de desplegar-les totes. */
const MAX_GUARDIA = 5;

function senseCobertura(a: GuardiaAreaDto): boolean {
  return a.cobertura === 'SENSE_COBERTURA' || !a.tecnic?.titularNom;
}

/** Pista d'estat a la dreta de cada activació en curs (escalat, pendents o tot confirmat). */
function Pista({ a }: { a: ActivacioResumDto }) {
  const { t } = useTranslation('operacio');
  if (a.total > 0 && a.confirmats >= a.total) {
    return <span className="badge badge--ok">{t('centre.totsConfirmats')}</span>;
  }
  if (a.nivellEscalat > 1) {
    return (
      <span className="badge badge--incident">
        {t('centre.escalada', { nivell: a.nivellEscalat })}
      </span>
    );
  }
  if (a.tipus === 'AVIS' && !a.critica) {
    return <span className="dim t-footnote">{t('centre.informatiu')}</span>;
  }
  return (
    <span className="dim t-footnote">
      {t('centre.pendents', { count: Math.max(0, a.total - a.confirmats) })}
    </span>
  );
}

function CercaCentre() {
  const { t } = useTranslation('operacio');
  const navigate = useNavigate();
  const [text, setText] = useState('');
  return (
    <form
      role="search"
      className="search centre__cerca"
      onSubmit={(e) => {
        e.preventDefault();
        const q = text.trim();
        if (!q) return;
        navigate(`/activacions?text=${encodeURIComponent(q)}`);
      }}
    >
      <Icona nom="magnifier" />
      <input
        type="search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('centre.cerca')}
        aria-label={t('centre.cercaEtiqueta')}
      />
    </form>
  );
}

/**
 * W-02 Centre de control: fusiona el Tauler (W-02), el Seguiment en directe (W-06) i la Guàrdia
 * actual (D-01). Tres indicadors, la llista d'activacions en curs amb el progrés de confirmació,
 * el que necessita atenció (buits de cobertura i canvis de torn) i qui és de guàrdia ara.
 */
export function W02Tauler() {
  const { t } = useTranslation('operacio');
  const f = useFormat();
  const { pot } = useMe();
  const [ara] = useState(() => new Date());
  const obertesQ = useObertes();
  const tauler = useTauler();
  const veuGuardies = pot('cai.guardies', 'CONSULTAR');
  const veuCalendaris = pot('cai.calendaris', 'CONSULTAR');
  const veuSollicituds = pot('cai.sollicituds', 'CONSULTAR');
  const guardies = useGuardiesAra(veuGuardies);
  const sollicituds = useSollicituds('PENDENT', veuSollicituds);
  const [totesArees, setTotesArees] = useState(false);
  useTempsRealTauler();

  const obertes = obertesQ.items;
  const arees = useMemo<GuardiaAreaDto[]>(
    () => guardies.data?.departaments.flatMap((d) => d.arees) ?? [],
    [guardies.data],
  );
  const tecnicPerArea = (area?: string | null) =>
    arees.find((a) => a.nom === area)?.tecnic?.titularNom ?? null;

  // KPI del servidor (RF-026); si encara no hi és, s'aproximen amb les activacions obertes.
  const kpi = useMemo(() => {
    const confirmats = obertes.reduce((s, a) => s + a.confirmats, 0);
    const destinataris = obertes.reduce((s, a) => s + a.total, 0);
    const local = {
      obertes: obertes.length,
      obertesCrisi: obertes.filter((a) => a.areaTipus === 'CRISI').length,
      percentatgeConfirmacio: destinataris > 0 ? (confirmats / destinataris) * 100 : null,
      confirmats,
      destinataris,
      areesAmbGuardia: arees.filter((a) => !senseCobertura(a)).length,
      areesTotals: arees.length,
    };
    return tauler.data ? { ...local, ...tauler.data } : local;
  }, [tauler.data, obertes, arees]);

  const critiques = obertes.filter((a) => a.critica).length;
  const buits = arees.filter(senseCobertura);
  const pendentsTorn = sollicituds.data?.length ?? 0;
  const deGuardia = arees.filter((a) => a.tecnic?.titularNom);
  const visiblesGuardia = totesArees ? deGuardia : deGuardia.slice(0, MAX_GUARDIA);
  const pct = kpi.percentatgeConfirmacio;
  const forats = kpi.areesTotals - kpi.areesAmbGuardia;

  return (
    <section className="pantalla" data-screen="W-02">
      <Toolbar
        titol={t('titols.tauler')}
        subtitol={t('centre.subtitol', { data: f.dataLlarga(ara), hora: f.hora(ara) })}
      >
        <CercaCentre />
        <Permis recurs="cai.activacions" tipus="ALTA">
          <Link className="btn btn--primary" to="/activacions/nova">
            <Icona nom="plus" />
            {t('nav.novaActivacio')}
          </Link>
        </Permis>
      </Toolbar>
      <div className="content stack centre-control">
        {obertesQ.isError ? <ErrorCarrega onReintenta={obertesQ.refetch} /> : null}
        <section className="centre__kpi" aria-label={t('centre.araMateix')}>
          <div className="stat" data-kpi="obertes">
            <span className="stat__label">{t('centre.kpi.obertes')}</span>
            <div className="stat__fila">
              <strong className="stat__value">{f.numero(kpi.obertes)}</strong>
              {critiques > 0 ? (
                <span className="badge badge--critic">
                  {t('centre.kpi.critiques', { count: critiques })}
                </span>
              ) : null}
              {kpi.obertesCrisi > 0 ? (
                <span className="badge badge--crisi">
                  {t('centre.kpi.crisi', { count: kpi.obertesCrisi })}
                </span>
              ) : null}
            </div>
          </div>
          <div className="stat" data-kpi="confirmacions">
            <span className="stat__label">{t('centre.kpi.confirmacions')}</span>
            <div className="stat__fila">
              <strong className="stat__value">{pct != null ? f.percentatge(pct) : '—'}</strong>
              <span className="stat__delta">
                {t('centre.kpi.confirmacionsDetall', {
                  confirmats: kpi.confirmats,
                  total: kpi.destinataris,
                })}
              </span>
            </div>
            <span
              className="progress"
              role="progressbar"
              aria-label={t('centre.kpi.confirmacions')}
              aria-valuenow={Math.round(pct ?? 0)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <i style={{ width: `${Math.round(pct ?? 0)}%` }} />
            </span>
          </div>
          {veuGuardies || tauler.data ? (
            <div className="stat" data-kpi="cobertura">
              <span className="stat__label">{t('centre.kpi.cobertura')}</span>
              <div className="stat__fila">
                <strong className="stat__value">
                  {kpi.areesAmbGuardia}/{kpi.areesTotals}
                </strong>
                {forats > 0 ? (
                  <span className="badge badge--incident">
                    {t('centre.kpi.buits', { count: forats })}
                  </span>
                ) : kpi.areesTotals > 0 ? (
                  <span className="badge badge--ok">{t('centre.kpi.totCobert')}</span>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>

        <div className="centre__cos">
          <section className="panel centre__curs" aria-labelledby="centre-curs">
            <div className="panel__head panel__head--net">
              <h2 id="centre-curs">{t('centre.enCurs')}</h2>
              <span className="dim t-footnote">{t('centre.perUrgencia')}</span>
              <div className="spacer" />
              <Link className="enllac" to="/activacions">
                {t('tauler.veureTotes')}
              </Link>
            </div>
            {obertes.length === 0 ? (
              <Buit icona="checkmark-circle">
                {obertesQ.isLoading ? t('comu:sessio.carregant') : t('tauler.senseObertes')}
              </Buit>
            ) : (
              <ul className="centre__llista">
                {obertes.map((a) => {
                  const tecnic = tecnicPerArea(a.area);
                  return (
                    <li key={a.id}>
                      <Link
                        className={`centre__fila${a.critica ? ' is-critica' : ''}`}
                        to={`/activacions/${a.id}`}
                        data-activacio={a.id}
                      >
                        <span className="centre__info">
                          <span className="hstack gap-8">
                            <BadgeTipus tipus={a.tipus} critica={a.critica} />
                            {a.areaTipus === 'CRISI' ? (
                              <span className="badge badge--crisi">
                                {t('comu:tipusArea.CRISI')}
                              </span>
                            ) : null}
                            <span className="codi">{a.codi}</span>
                            <span className="dim t-caption">
                              · {textFa(minutsDes(a.createdAt, ara.getTime()) ?? 0, t)}
                            </span>
                          </span>
                          <strong className="centre__titol">{a.titol}</strong>
                          <span className="dim t-footnote">
                            {[
                              a.area,
                              a.servei,
                              tecnic ? t('centre.deGuardiaNom', { nom: tecnic }) : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                        <ProgresConfirmacions
                          confirmats={a.confirmats}
                          total={a.total}
                          color={colorConfirmacions(a)}
                          etiqueta={t('centre.confirmats')}
                        />
                        <span className="centre__pista">
                          <Pista a={a} />
                        </span>
                        <Icona nom="chevron-right" className="centre__chev" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <div className="stack centre__lateral">
            {veuGuardies || veuSollicituds ? (
              <section className="panel" aria-labelledby="centre-atencio">
                <div className="panel__head panel__head--net">
                  <h2 id="centre-atencio">{t('centre.atencio')}</h2>
                </div>
                <div className="panel__body stack stack--s">
                  {buits.length === 0 && pendentsTorn === 0 ? (
                    <p className="dim t-footnote" style={{ margin: 0 }}>
                      {t('centre.totEnOrdre')}
                    </p>
                  ) : null}
                  {buits.map((a) => (
                    <div className="centre__avis" key={a.id} data-buit={a.id}>
                      <span className="badge badge--incident">{t('centre.buit')}</span>
                      <div className="stack stack--s">
                        <span>{t('centre.buitText', { area: a.nom })}</span>
                        {veuCalendaris ? (
                          <Link className="btn btn--s" to="/planificacio/calendaris">
                            {t('centre.assignaGuardia')}
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  {pendentsTorn > 0 ? (
                    <div className="centre__avis" data-sollicituds={pendentsTorn}>
                      <span className="badge badge--avis">{pendentsTorn}</span>
                      <div className="stack stack--s">
                        <span>{t('centre.sollicitudsText', { count: pendentsTorn })}</span>
                        <Link className="btn btn--s" to="/planificacio/sollicituds">
                          {t('centre.revisa')}
                        </Link>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            {veuGuardies ? (
              <section className="panel" aria-labelledby="centre-guardia">
                <div className="panel__head panel__head--net">
                  <h2 id="centre-guardia">{t('centre.deGuardia')}</h2>
                  <div className="spacer" />
                  {veuCalendaris ? (
                    <Link className="enllac" to="/planificacio/calendaris">
                      {t('centre.calendari')}
                    </Link>
                  ) : null}
                </div>
                <div className="panel__body stack stack--s">
                  {guardies.isError ? (
                    <ErrorCarrega onReintenta={() => void guardies.refetch()} />
                  ) : deGuardia.length === 0 ? (
                    <Buit icona="person-2">
                      {guardies.isLoading ? t('comu:sessio.carregant') : t('tauler.senseArees')}
                    </Buit>
                  ) : (
                    visiblesGuardia.map((a) => {
                      const nom = a.tecnic?.titularNom ?? '';
                      return (
                        <div className="hstack gap-12" key={a.id} data-area-id={a.id}>
                          <Avatar nom={nom} mida="m" crisi={a.tipus === 'CRISI'} />
                          <div className="list-row__text">
                            <div className="list-row__label strong">{nom}</div>
                            <div className="list-row__sub">
                              {[
                                a.nom,
                                a.tecnic?.substitutNom
                                  ? t('tauler.substitut', { nom: a.tecnic.substitutNom })
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          </div>
                          <BotoTrucada nom={nom} telefon={a.tecnic?.titularTelefon} />
                        </div>
                      );
                    })
                  )}
                  {deGuardia.length > MAX_GUARDIA ? (
                    <button
                      type="button"
                      className="enllac"
                      aria-expanded={totesArees}
                      onClick={() => setTotesArees((v) => !v)}
                    >
                      {totesArees
                        ? t('centre.mostraMenys')
                        : t('centre.mostraTotes', { count: arees.length })}
                    </button>
                  ) : null}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
